import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { map, isEqual, find, isEmpty } from 'lodash'

import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { useMediaQuery, useTheme } from '@material-ui/core'

import { ControlledTextFieldProps, KeyAndLabelSelectType, KeyLabelType } from '../../../types'
import { handleLatinKeysDown } from '../../../utils'
import { StyledCheckbox } from '../StyledComponents'
import { MobileMultipleSelector } from './MobileMultipleSelector'
import { TextField, TextFieldProps } from '@mui/material'
import { StyledAutocomplete } from './StyledComponents'

interface FormAutocompleteSelectProps {
  name: string
  data: KeyAndLabelSelectType[]
  disableClearable?: boolean
  readOnly?: boolean
  isForModal?: boolean
  doubleRows?: boolean
}

const defaultRules = {} //{ required: true }

export const FormAutocompleteMultiSelectComponent: React.FC<
  FormAutocompleteSelectProps & Partial<ControlledTextFieldProps>
> = ({
  name = '',
  disableClearable,
  data,
  shouldValidateParam,
  readOnly = false,
  isForModal = false,
  rules,
  defaultValue,
  doubleRows,
  ...rest
}) => {
  const { errors, register, setValue, watch } = useFormContext()
  const error = errors ? errors[name || 'FormAutocompleteSelect'] : null

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [inputValue, setInputValue] = useState('')
  const [selectedOptions, setSelectedOptions] = useState<KeyAndLabelSelectType[]>([])

  const currentValue = watch(name)

  const handleInputChange = useCallback((_: ChangeEvent<unknown>, newValue: string) => {
    setInputValue(newValue)
  }, [])

  const handleChange = useCallback(
    (_: ChangeEvent<unknown>, value: KeyAndLabelSelectType[]) => {
      const keys = map(value, (item) => item.key)
      setValue(name, keys, { shouldDirty: true, shouldValidate: true })
      setSelectedOptions(value)
    },
    [currentValue],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        const matchedOption = isEmpty(inputValue.trim())
          ? data[0]
          : data.find((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))

        if (matchedOption) {
          setSelectedOptions((prevState) => [...prevState, matchedOption])
          setValue(
            name,
            currentValue ? [...currentValue, matchedOption.key] : [matchedOption.key],
            { shouldDirty: true, shouldValidate: true },
          )
          setInputValue('')
        }
        event.preventDefault()
      }

      handleLatinKeysDown(event)
    },
    [data, inputValue, currentValue],
  )

  useEffect(() => {
    if (isEmpty(selectedOptions) && !isEmpty(currentValue)) {
      setSelectedOptions(
        data?.filter((itemData) =>
          find(currentValue, (itemCurrentValue) => itemCurrentValue === itemData.key),
        ),
      )
    }
  }, [JSON.stringify(currentValue)])

  useEffect(() => {
    if (defaultValue) {
      const defaultDataOptions = data?.filter((itemData) =>
        find(defaultValue, (itemCurrentValue) => itemCurrentValue === itemData.key),
      )
      setSelectedOptions(defaultDataOptions)
    }
  }, [defaultValue, data])

  useEffect(() => {
    register(name, { ...defaultRules, ...(rules ?? {}) })
  }, [register, name, rules])

  return !isSmallScreen ? (
    <StyledAutocomplete
      value={selectedOptions}
      multiple
      sx={{
        maxHeight: isForModal ? '50vh' : 'auto',
        overflowY: error ? 'unset' : 'inherit',
        ...(readOnly && {
          '& div': {
            backgroundColor: '#F5F5F5',
          },
          '& .MuiFormLabel-root': {
            zIndex: 1,
          },
          '& .MuiInput-root': {
            backgroundColor: '#F5F5F5',
            pointerEvents: 'none',
          },
          '& .MuiIconButton-root, & .MuiInputBase-root': {
            color: '#999999',
          },
        }),
      }}
      slotProps={{
        paper: {
          sx: {
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              position: 'relative',
              paddingRight: 40,
              backgroundColor: 'transparent',
              '&::after': {
                display: 'none',
              },
            },
            '& .MuiAutocomplete-option[data-focus="true"]': {
              color: '#434343',
            },
          },
        },
      }}
      options={data}
      getOptionLabel={(option) => option.label}
      onChange={handleChange}
      disableCloseOnSelect
      disabled={readOnly}
      renderInput={(params) => (
        <TextField
          variant="standard"
          margin="none"
          name={name}
          {...params}
          error={!!error}
          helperText={error ? error.message : null}
          {...(rest as TextFieldProps)}
          slotProps={{
            htmlInput: {
              readOnly,
              ...params.inputProps,
            },
          }}
          onKeyDown={handleKeyDown}
          data-test={`autotest-${name}`}
          multiline
        />
      )}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <StyledCheckbox checked={selected} />
          {option.label}
        </li>
      )}
      openText={''}
      closeText={''}
      popupIcon={<ExpandMoreIcon color="disabled" />}
      openOnFocus={!readOnly}
      onInputChange={handleInputChange}
    />
  ) : (
    <MobileMultipleSelector
      name={name}
      optionsList={data as KeyLabelType[]}
      {...rest}
      doubleRows={doubleRows}
    />
  )
}
export const FormAutocompleteMultiSelect = React.memo(FormAutocompleteMultiSelectComponent, isEqual)
